import React from 'react';
import NextLink from 'next/link';
import { cn } from '@/lib/cn';
import { IconChevronRight } from '@repo/svgs';

type LinkVariant = 'default' | 'footer';

export interface LinkBaseProps {
  href: string;
  variant?: LinkVariant;
  external?: boolean;
  trailingIcon?: boolean;
}

type ExternalLinkProps = LinkBaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    external: true;
  };

type InternalLinkProps = LinkBaseProps &
  Omit<React.ComponentProps<typeof NextLink>, 'href'> & {
    external?: false;
  };

export type LinkProps = ExternalLinkProps | InternalLinkProps;

const variantClasses: Record<LinkVariant, string> = {
  default:
    'text-accent hover:text-accent-hover hover:underline underline-offset-2 text-body-2-high',
  footer: 'text-secondary hover:underline underline-offset-2 text-caption',
};

export function Link({
  href,
  children,
  className,
  variant = 'default',
  external = false,
  trailingIcon = false,
  ...props
}: LinkProps) {
  const rel = external ? cn('noopener', (props as ExternalLinkProps).rel) : (props as any).rel;
  const target = external ? '_blank' : (props as any).target;

  const classes = cn(
    'inline-flex items-center gap-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
    variantClasses[variant],
    className,
  );

  if (external) {
    return (
      <a
        href={href}
        className={classes}
        rel={rel}
        target={target}
        {...(props as Omit<ExternalLinkProps, keyof LinkBaseProps>)}
      >
        {children}
      </a>
    );
  }

  return (
    <NextLink
      href={href}
      className={classes}
      {...(props as Omit<InternalLinkProps, keyof LinkBaseProps>)}
    >
      {children}
    </NextLink>
  );
}

export default Link;
